import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { LICENSE_DEFINITIONS } from '@components/shared/license/license.constants';
import { ChartTooltipTrigger } from '@components/ui-chart/base-chart.types';
import {
    ILicenseUsageData,
    Interval,
    ODataResponse,
    RobotType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    DEFAULT_USAGE_INTERVALS,
    intervalToUnit,
    unitToFormat,
} from '@pages/shared/chart.config';
import {
    ChartColorService,
    ChartService,
    LicenseUsageService,
    ODataQueryOptions,
    ResourceMapperService,
    StorageService,
    TenantService,
} from '@services';
import {
    ISuggestValue,
    ISuggestValues,
} from '@uipath/angular/components/ui-suggest';
import { unsubscribe } from '@utils/object.utils';

import constant from 'lodash-es/constant';
import findKey from 'lodash-es/findKey';
import groupBy from 'lodash-es/groupBy';
import times from 'lodash-es/times';
import values from 'lodash-es/values';
import * as moment from 'moment';
import {
    BehaviorSubject,
    combineLatest,
    merge,
    Observable,
    Subscription,
} from 'rxjs';
import {
    map,
    tap,
} from 'rxjs/operators';

interface ILicenseUsageLineChart {
    loading: boolean;
    hasData: boolean;
    data: number[] | null;
    legend: string[] | null;
    labels: string[] | null;
    colors: string[];
}

@Component({
    selector: 'ui-license-chart',
    templateUrl: './ui-license-chart.component.html',
    styleUrls: ['./ui-license-chart.component.scss'],
    providers: [
        ChartService,
        LicenseUsageService,
        TenantService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiLicenseChartComponent implements OnInit, OnDestroy {
    @Input()
    public isHost!: boolean;

    public ChartTooltipTrigger = ChartTooltipTrigger;
    public defaultUsageIntervals = DEFAULT_USAGE_INTERVALS;

    public chart: ILicenseUsageLineChart = {
        loading: true,
        hasData: false,
        data: null,
        legend: null,
        labels: null,
        colors: this._chartColorService.license,
    };

    public chartInterval$: BehaviorSubject<Interval>;
    public intervalLabel$!: Observable<string>;

    public tenantId?: number;
    public defaultTenant: ISuggestValue[] | null = null;

    public get labelFormat(): string {
        return this._labelFormat;
    }
    public set labelFormat(value: string) {
        this._labelFormat = unitToFormat(value);
    }

    private _labelFormat = '';
    private _licenseUsageSub?: Subscription;

    private _tenantCacheKey: string;
    private _intervalCacheKey: string;

    constructor(
        private _appState: AppStateFacade,
        private _translateService: TranslateService,
        private _licenseUsageService: LicenseUsageService,
        private _chartService: ChartService,
        private _tenantService: TenantService,
        private _storageService: StorageService,
        private _cd: ChangeDetectorRef,
        private _router: Router,
        private _chartColorService: ChartColorService,
    ) {
        const buildVersion = this._appState.authConfig.buildVersion;
        const userId = this._appState.user.value!.id;

        this._tenantCacheKey = `${buildVersion}-${userId}-license-chart-tenant`;
        this._intervalCacheKey = `${this._router.url} chart - ${userId} - LicenseUsage`;
        this.chartInterval$ = new BehaviorSubject(this._storageService.get(this._intervalCacheKey, Interval.Week));
    }

    ngOnInit() {
        if (!this.isHost) {
            this.tenantId = this._appState.user.value!.tenancyId;
        }

        this.intervalLabel$ = merge(
            this._appState.language.change$,
            this.chartInterval$,
        ).pipe(
            map(_ => this._chartService.getLicenseUsageLabelForInterval(this.chartInterval$.value)),
        );

        this.loadChart();
    }

    ngOnDestroy() {
        unsubscribe(
            this._licenseUsageSub,
        );
    }

    public tenantsSearchFactory = (query = '', fetchSize = 20, skipNum: number = 0): Observable<ISuggestValues<any>> => {
        const queryOptions: ODataQueryOptions = {
            orderBy: ['Name'],
            top: fetchSize,
            skip: skipNum,
            filter: {
                Name: {
                    contains: query,
                },
            },
        };

        return this._tenantService
            .get(queryOptions)
            .pipe(
                map((response) => this._mapTenants(response)),
                tap(({ data = [] }) => {
                    const tenants = data;

                    if (
                        !this.isHost ||
                        this.defaultTenant != null ||
                        tenants.length === 0
                    ) { return; }

                    const cachedTenant = this._storageService.get(this._tenantCacheKey, null);
                    let defaultTenant;

                    defaultTenant = cachedTenant || tenants[0];

                    this.defaultTenant = [defaultTenant];
                    this._cd.markForCheck();
                }),
            );
    }

    public onTenantSelect(tenant: ISuggestValue) {
        this.tenantId = tenant.id as number;

        this._storageService.set(
            this._tenantCacheKey,
            tenant,
        );

        this.loadChart();
    }

    public onIntervalChange(licenseUsageInterval: Interval) {
        if (licenseUsageInterval !== null) {
            this.chartInterval$.next(licenseUsageInterval);
        }
        this.loadChart();
        this._storageService.set(this._intervalCacheKey, this.chartInterval$.value);
    }

    public loadChart() {
        if (
            this.isHost &&
            this.tenantId == null
        ) {
            this.chart.hasData = false;
            return;
        }

        this.chart.loading = true;

        this.labelFormat = intervalToUnit(this.chartInterval$.value);

        if (this._licenseUsageSub) { this._licenseUsageSub.unsubscribe(); }

        this._licenseUsageSub =
            combineLatest([
                this._licenseUsageService
                    .getTenantStatsById(
                        this.tenantId!,
                        this.chartInterval$.value,
                    ),
                this._translateService
                    .stream(values(ResourceMapperService.robotType)),
            ])
                .subscribe(([licenseUsageStats, licenseLabels]) => {
                    if (licenseUsageStats.length > 0) {
                        this.chart.hasData = true;
                        [
                            this.chart.legend,
                            this.chart.labels,
                            this.chart.data,
                        ] = this._extractChartData(licenseUsageStats, licenseLabels);
                    } else {
                        this.chart.hasData = false;
                    }

                    this.chart.loading = false;
                    this._cd.markForCheck();
                });
    }

    private _extractChartData(data: ILicenseUsageData[], licenseLabels: Record<string, string>) {
        const robotTypeIdsByLicenseType: any[] = [];
        const chartData = [];
        const chartLegend = [];
        let chartLabels: any[] = [];

        const groups = groupBy(data, 'robotType');

        // Datasets should match the order in licenseDefinitions.
        LICENSE_DEFINITIONS.forEach(licenseDefinition => {
            robotTypeIdsByLicenseType.push(
                findKey(
                    RobotType,
                    key => key.toString().toLowerCase() === licenseDefinition.key.toLowerCase(),
                ),
            );
        });

        for (const robotTypeId of robotTypeIdsByLicenseType) {
            chartLegend.push(
                licenseLabels[ResourceMapperService.robotType[RobotType[robotTypeId]]],
            );

            if (groups.hasOwnProperty(robotTypeId)) {
                chartData.push(groups[robotTypeId].map(singleRobotStat => singleRobotStat.count));

                if (chartLabels.length === 0) {
                    // Only extract the date labels for one license type, since all of them have the same timestamps at this point.
                    chartLabels = groups[robotTypeId].map(
                        singleRobotStat => moment(singleRobotStat.timestamp)
                            .tz(this._appState.settings.get(Setting.timezone)!)
                            .format(this.labelFormat),
                    );
                }
            } else {
                // Generate empty data for missing license types, so that they would also appear in the legend.
                chartData.push(times(
                    groups[
                        data[0].robotType // When this method is called, there will always be at least one item in the data array.
                    ].length, // Use as template the number of datapoints generated for the first returned license type.
                    constant(0),
                ));
            }
        }

        return [chartLegend, chartLabels, chartData];
    }

    private _mapTenants = (tenants: ODataResponse<any>): ISuggestValues<any> => {
        const mappedTenants: ISuggestValue[] = tenants.data
            .map(tenant => ({
                id: tenant.id,
                text: tenant.name,
            }));
        return {
            data: mappedTenants,
            total: tenants.total,
        };
    }
}
